import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchContacts,
  fetchAllContacts,
  fetchContact,
  createContact,
  updateContact,
  deleteContact,
  restoreContact,
} from '@/lib/api/contacts'
import { broadcastInvalidation } from '@/lib/sync'
import type { ContactFormData, ContactInsert } from '@/types/contacts'
import type { RecordStatusFilter } from '@/types/record-status'
import { clientDetailKeys } from './useClientDetail'
import { clientRegistryKeys } from './useClientsRegistry'
import { toast } from 'sonner'

/**
 * Query key factory for contacts
 * Provides consistent keys for cache management
 */
export const contactKeys = {
  all: ['contacts'] as const,
  lists: () => [...contactKeys.all, 'list'] as const,
  list: (clientId: string, recordStatus?: RecordStatusFilter) =>
    [...contactKeys.lists(), clientId, recordStatus ?? 'active'] as const,
  allContacts: (recordStatus?: RecordStatusFilter) =>
    [...contactKeys.lists(), 'all', recordStatus ?? 'active'] as const,
  details: () => [...contactKeys.all, 'detail'] as const,
  detail: (id: string) => [...contactKeys.details(), id] as const,
}

/**
 * Fetch all contacts for a client
 */
export function useContacts(clientId: string | undefined, recordStatus: RecordStatusFilter = 'active') {
  return useQuery({
    queryKey: contactKeys.list(clientId || '', recordStatus),
    queryFn: () => fetchContacts(clientId!, recordStatus),
    enabled: !!clientId,
    refetchOnWindowFocus: true,
  })
}

/**
 * Fetch all contacts across all clients
 */
export function useAllContacts(recordStatus: RecordStatusFilter = 'active') {
  return useQuery({
    queryKey: contactKeys.allContacts(recordStatus),
    queryFn: () => fetchAllContacts(recordStatus),
    refetchOnWindowFocus: true,
  })
}

/**
 * Fetch a single contact by ID
 */
export function useContact(id: string | undefined) {
  return useQuery({
    queryKey: contactKeys.detail(id || ''),
    queryFn: () => fetchContact(id!),
    enabled: !!id,
  })
}

/**
 * Create a new contact
 */
export function useCreateContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ContactInsert) => createContact(data),
    onSuccess: (_, variables) => {
      const keys = [contactKeys.list(variables.client_id), clientDetailKeys.contacts(variables.client_id), clientRegistryKeys.all] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Contact created successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to create contact', {
        description: error.message,
      })
    },
  })
}

/**
 * Update an existing contact
 */
export function useUpdateContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      clientId: string
      data: Partial<ContactFormData>
    }) => updateContact(id, data),
    onSuccess: (_, { id, clientId }) => {
      const keys = [contactKeys.list(clientId), contactKeys.detail(id), clientDetailKeys.contacts(clientId), clientRegistryKeys.all] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Contact updated successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to update contact', {
        description: error.message,
      })
    },
  })
}

/**
 * Delete a contact (soft delete)
 */
export function useDeleteContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id }: { id: string; clientId: string }) => deleteContact(id),
    onSuccess: (_, { clientId }) => {
      const keys = [contactKeys.list(clientId), contactKeys.lists(), clientDetailKeys.contacts(clientId), clientRegistryKeys.all] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Contact deleted successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to delete contact', {
        description: error.message,
      })
    },
  })
}

/**
 * Restore a soft-deleted contact
 */
export function useRestoreContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => restoreContact(id),
    onSuccess: () => {
      const keys = [contactKeys.lists(), clientRegistryKeys.all] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Contact restored successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to restore contact', {
        description: error.message,
      })
    },
  })
}
